import React, { useEffect } from 'react';
import { domainUrl, ROUTES } from './domainConfig';
import { DADA, TornLabel, DadaBackdrop, DadaStyles } from './components/dada';
import { useTranslation } from './i18n';

export default function WeRepresentPage({ onNavigate }) {
  const { t, language } = useTranslation();
  const kandidaatUrl = domainUrl('kandidaat', '/');
  const bedrijvenUrl = domainUrl('bedrijven', ROUTES.bedrijvenLanding);
  const manifestLine2 = language === 'nl'
    ? 'Solliciteren wordt sterker wanneer je het werk eerst echt kunt ervaren. WeRepresent maakt talent zichtbaar en organisaties zekerder.'
    : 'Applying becomes stronger when you can truly experience the work first. WeRepresent makes talent visible and gives organisations more certainty.';
  const manifestLine3 = language === 'nl'
    ? 'Zo ontstaat een match die klopt, voor mens en organisatie.'
    : 'That is how a match emerges that truly fits, for people and organisation.';

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'WeRepresent.nl';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#f6f1e8] text-[#151827]">
      <DadaStyles />
      <DadaBackdrop />

      {/* HEADER */}
      <header className="relative z-20 mx-auto flex w-full max-w-6xl items-center justify-between px-5 py-6 sm:px-8">
        <div className="flex items-center gap-3">
          <img src="/logozondertekst.png" alt="WeRepresent" className="h-9 w-auto" />
          <div className="leading-none">
            <div className="text-lg font-black tracking-tight text-[#151827] md:text-xl">WeRepresent.nl</div>
          </div>
        </div>
        <nav className="hidden items-center gap-2 sm:flex">
          <button
            onClick={() => onNavigate(kandidaatUrl)}
            className="rounded-full border-4 border-[#151827] bg-white px-4 py-2 text-sm font-black uppercase text-[#151827] transition hover:-translate-y-0.5"
            style={{ boxShadow: '4px 4px 0 #3f7f65' }}
          >
            BetaaldSolliciteren
          </button>
          <button
            onClick={() => onNavigate(bedrijvenUrl)}
            className="rounded-full bg-[#151827] px-4 py-2 text-sm font-black uppercase text-white transition hover:-translate-y-0.5"
            style={{ boxShadow: '4px 4px 0 #713d8d' }}
          >
            werepresenttalent
          </button>
        </nav>
      </header>

      {/* HERO / MANIFEST */}
      <section className="relative z-10 mx-auto max-w-6xl px-5 pb-12 pt-4 sm:px-8 lg:pb-16">
        <div className="animate-dada-in">
          <div className="mb-7 flex flex-wrap justify-center gap-3">
            <TornLabel className="-rotate-2" bgColor={DADA.green} textColor="#ffffff">{t('werepresent.labels.honest')}</TornLabel>
            <TornLabel className="rotate-1" bgColor="#ffffff" textColor={DADA.ink}>{t('werepresent.labels.transparent')}</TornLabel>
            <TornLabel className="-rotate-1" bgColor={DADA.purple} textColor="#ffffff">{t('werepresent.labels.data_driven')}</TornLabel>
          </div>

          <h1 className="text-center text-[clamp(3rem,12vw,9rem)] font-black leading-[0.85] tracking-[-0.05em]">
            <span className="inline-block -rotate-1">We</span>
            <span className="inline-block text-[#713d8d]">Re</span>
            <span className="inline-block rotate-1">present</span>
          </h1>

          <p className="mx-auto mt-8 max-w-3xl text-center text-xl font-semibold leading-snug text-[#2a2f40] sm:text-2xl">
            {t('werepresent.hero_subtitle')}
          </p>
        </div>

        <div
          className="relative mt-12 -rotate-1 rounded-[2rem] border-4 border-[#151827] bg-[#151827] p-8 text-white md:p-12 animate-dada-in-delayed"
          style={{ boxShadow: '18px 18px 0 #713d8d' }}
        >
          <div className="absolute -top-5 left-8">
            <TornLabel className="rotate-2" bgColor="#ffffff" textColor={DADA.ink}>{t('werepresent.manifest_badge')}</TornLabel>
          </div>
          <h2 className="sr-only">{t('werepresent.manifest_badge')}</h2>
          <div className="mt-4 space-y-4 text-lg leading-relaxed text-gray-200 md:text-xl">
            <p>{t('werepresent.manifest.line_1')}</p>
            <p>{manifestLine2}</p>
            <p className="text-2xl font-black italic text-[#8fd6b6]">
              {manifestLine3}
            </p>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
          <div
            className="rotate-1 rounded-[1.75rem] border-4 border-[#151827] bg-white p-7 text-left"
            style={{ boxShadow: '10px 10px 0 #3f7f65' }}
          >
            <TornLabel className="-rotate-2" bgColor={DADA.green} textColor="#ffffff">{t('werepresent.for_talent.title')}</TornLabel>
            <h3 className="sr-only">{t('werepresent.for_talent.title')}</h3>
            <div className="mt-5 space-y-3 text-lg text-[#2a2f40]">
              <p>{t('werepresent.for_talent.line_1')}</p>
              <p>{t('werepresent.for_talent.line_2')}</p>
              <p>{t('werepresent.for_talent.line_3')}</p>
              <p className="text-xl font-black italic text-[#3f7f65]">
                {t('werepresent.for_talent.line_4')}
              </p>
            </div>
          </div>

          <div
            className="-rotate-1 rounded-[1.75rem] border-4 border-[#151827] bg-white p-7 text-left"
            style={{ boxShadow: '10px 10px 0 #713d8d' }}
          >
            <TornLabel className="rotate-2" bgColor={DADA.purple} textColor="#ffffff">{t('werepresent.for_orgs.title')}</TornLabel>
            <h3 className="sr-only">{t('werepresent.for_orgs.title')}</h3>
            <div className="mt-5 space-y-3 text-lg text-[#2a2f40]">
              <p>{t('werepresent.for_orgs.line_1')}</p>
              <p>{t('werepresent.for_orgs.line_2')}</p>
              <p>{t('werepresent.for_orgs.line_3')}</p>
              <p className="text-xl font-black italic text-[#713d8d]">
                {t('werepresent.for_orgs.line_4')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="relative z-10 overflow-hidden border-y-4 border-[#151827] bg-[#151827] py-5 text-white">
        <div className="whitespace-nowrap text-2xl font-black uppercase tracking-tight sm:text-3xl animate-marquee">
          {t('werepresent.marquee')}
        </div>
      </section>

      {/* ONZE MERKEN */}
      <section className="relative z-10 mx-auto max-w-6xl px-5 py-16 sm:px-8 md:py-24">
        <div className="mb-12 text-center">
          <TornLabel className="rotate-1" bgColor="#ffffff" textColor={DADA.ink}>{t('werepresent.brands.badge')}</TornLabel>
          <h2 className="mt-6 text-[clamp(2rem,6vw,3.8rem)] font-black leading-[0.95] tracking-[-0.03em]">
            {t('werepresent.brands.title')}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg font-medium text-[#2a2f40]/80">
            {t('werepresent.brands.body')}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <div
            className="rotate-1 rounded-[2rem] border-4 border-[#151827] bg-white p-8 transition hover:-translate-y-1"
            style={{ boxShadow: '14px 14px 0 #3f7f65' }}
          >
            <div className="mb-4 flex items-center justify-between">
              <img src="/logozondertekst.png" alt="BetaaldSolliciteren" className="h-12" />
              <span className="text-2xl">✦</span>
            </div>
            <h3 className="text-3xl font-black text-[#3f7f65]">BetaaldSolliciteren.nl</h3>
            <p className="mt-2 text-base font-bold italic text-[#151827]/60">{t('auth.brand_tagline')}</p>
            <p className="mt-5 text-base text-[#2a2f40]">
              {t('werepresent.brands.talent_body')}
            </p>
            <button
              onClick={() => onNavigate(kandidaatUrl)}
              className="mt-7 w-full rounded-2xl border-4 border-[#151827] bg-[#3f7f65] py-4 text-lg font-black text-white transition hover:translate-x-1 hover:translate-y-1"
              style={{ boxShadow: '8px 8px 0 #151827' }}
            >
              {t('werepresent.brands.talent_cta')}
            </button>
          </div>

          <div
            className="-rotate-1 rounded-[2rem] border-4 border-[#151827] bg-white p-8 transition hover:-translate-y-1"
            style={{ boxShadow: '14px 14px 0 #713d8d' }}
          >
            <div className="mb-4 flex items-center justify-between">
              <img src="/logozondertekst.png" alt="werepresenttalent" className="h-12" />
              <span className="text-2xl">✦</span>
            </div>
            <h3 className="text-3xl font-black text-[#713d8d]">werepresenttalent.nl</h3>
            <p className="mt-2 text-base font-bold italic text-[#151827]/60">{t('werepresent.brands.company_tagline')}</p>
            <p className="mt-5 text-base text-[#2a2f40]">
              {t('werepresent.brands.company_body')}
            </p>
            <button
              onClick={() => onNavigate(bedrijvenUrl)}
              className="mt-7 w-full rounded-2xl border-4 border-[#151827] bg-[#713d8d] py-4 text-lg font-black text-white transition hover:translate-x-1 hover:translate-y-1"
              style={{ boxShadow: '8px 8px 0 #151827' }}
            >
              {t('werepresent.brands.company_cta')}
            </button>
          </div>
        </div>
      </section>

      {/* JURIDISCH */}
      <section className="relative z-10 bg-[#151827] py-16 text-white md:py-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="mb-12 text-center">
            <TornLabel className="-rotate-2" bgColor="#ffffff" textColor={DADA.ink}>{t('werepresent.legal.badge')}</TornLabel>
            <h2 className="mt-6 text-[clamp(2rem,6vw,3.8rem)] font-black leading-[0.95] tracking-[-0.03em]">
              {t('werepresent.legal.title')}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg font-medium text-white/70">
              {t('werepresent.legal.body')}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {[
              { path: '/privacy', icon: '🔒', title: t('werepresent.legal.cards.privacy.title'), body: t('werepresent.legal.cards.privacy.body'), accent: '#3f7f65', rotate: 'rotate-1' },
              { path: '/voorwaarden', icon: '📋', title: t('werepresent.legal.cards.terms.title'), body: t('werepresent.legal.cards.terms.body'), accent: '#713d8d', rotate: '-rotate-1' },
              { path: '/ai-policy', icon: '🤖', title: t('werepresent.legal.cards.ai.title'), body: t('werepresent.legal.cards.ai.body'), accent: '#713d8d', rotate: 'rotate-1' },
              { path: '/cookieverklaring', icon: '🍪', title: t('werepresent.legal.cards.cookies.title'), body: t('werepresent.legal.cards.cookies.body'), accent: '#3f7f65', rotate: '-rotate-1' }
            ].map((item) => (
              <button
                key={item.path}
                onClick={() => onNavigate(item.path)}
                className={`group rounded-[1.5rem] border-4 border-white bg-white p-6 text-left text-[#151827] transition hover:translate-x-1 hover:translate-y-1 ${item.rotate}`}
                style={{ boxShadow: `8px 8px 0 ${item.accent}` }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="flex h-14 w-14 flex-none items-center justify-center rounded-2xl border-4 border-[#151827] text-2xl"
                    style={{ backgroundColor: '#f6f1e8' }}
                  >
                    {item.icon}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-xl font-black">{item.title}</h3>
                    <p className="mt-2 text-sm font-medium text-[#2a2f40]">{item.body}</p>
                  </div>
                  <div className="flex-none text-2xl">→</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="relative z-10 mx-auto max-w-6xl px-5 py-20 sm:px-8 md:py-28">
        <div
          className="rotate-1 rounded-[2rem] border-4 border-[#151827] bg-[#713d8d] p-8 text-white md:p-14"
          style={{ boxShadow: '18px 18px 0 #151827' }}
        >
          <div className="flex flex-col items-center text-center">
            <TornLabel className="-rotate-2" bgColor="#ffffff" textColor={DADA.ink}>{t('werepresent.contact.badge')}</TornLabel>
            <h2 className="mt-6 text-[clamp(2rem,6vw,3.8rem)] font-black uppercase leading-[0.95] tracking-[-0.03em]">
              {t('werepresent.contact.title')}
            </h2>
            <p className="mx-auto mt-6 max-w-2xl text-lg font-semibold text-white/85 md:text-xl">
              {t('werepresent.contact.body')}
            </p>
            <a
              href="mailto:info@betaaldsolliciteren.nl"
              className="mt-8 inline-block rounded-2xl border-4 border-[#151827] bg-white px-8 py-4 text-lg font-black text-[#151827] transition hover:translate-x-1 hover:translate-y-1"
              style={{ boxShadow: '8px 8px 0 #151827' }}
            >
              info@betaaldsolliciteren.nl
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative z-10 bg-[#151827] py-12 text-white">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="flex items-center gap-4">
              <img src="/logozondertekst.png" alt="WeRepresent" className="h-9 brightness-0 invert" />
              <span className="text-2xl font-black uppercase">WeRepresent B.V.</span>
            </div>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 text-sm font-bold text-white/80">
              <button onClick={() => onNavigate(kandidaatUrl)} className="transition hover:text-white">BetaaldSolliciteren</button>
              <button onClick={() => onNavigate(bedrijvenUrl)} className="transition hover:text-white">werepresenttalent</button>
              <a href="mailto:info@betaaldsolliciteren.nl" className="transition hover:text-white">{t('werepresent.footer.contact')}</a>
              <button onClick={() => onNavigate('/privacy')} className="transition hover:text-white">{t('werepresent.footer.privacy')}</button>
              <button onClick={() => onNavigate('/voorwaarden')} className="transition hover:text-white">{t('werepresent.footer.terms')}</button>
              <button onClick={() => onNavigate('/ai-policy')} className="transition hover:text-white">AI Policy</button>
              <button onClick={() => onNavigate('/cookieverklaring')} className="transition hover:text-white">{t('werepresent.footer.cookies')}</button>
            </div>
          </div>
          <div className="mt-8 border-t border-white/15 pt-6 text-center text-sm text-white/55">
            {t('werepresent.footer.copyright', { year: new Date().getFullYear() })}
          </div>
        </div>
      </footer>
    </main>
  );
}
